// Critical-fields contract — see aegle-docs critical_fields_contract.md
/**
 * Dynamic column builder for the document review grid.
 *
 * Columns are derived from the actual extracted data — showing only fields
 * that have values and applying smart formatting based on field name conventions.
 * Returns `CompactTableColumn<ExtractedRecord>[]` for use with `CompactTable`.
 */

import { Typography } from "@mui/material";
import { CompactTableColumn } from "@/components/common/CompactTable";
import { formatDate } from "@/utils/formatters/dateFormatter";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { ExtractedRecord, TargetSchema, ValidAny } from "@/types";
import { getRecordData } from "../../utils/recordUtils";
import { CRITICAL_FIELDS } from "./criticalFields";

// =============================================================================
// Field format detection
// =============================================================================

const CURRENCY_SUFFIXES = ["_amount", "_cost", "_price", "_total", "_deductible", "_premium", "_paid", "_spend"];
const CURRENCY_PREFIXES = ["ytd_"];
const DATE_SUFFIXES = ["_date", "_datetime"];
const ID_SUFFIXES = ["_id", "_ref"];

type FieldFormat = "currency" | "date" | "id" | "text";

const detectFieldFormat = (fieldName: string): FieldFormat => {
  const lower = fieldName.toLowerCase();
  if (CURRENCY_SUFFIXES.some((s) => lower.endsWith(s))) return "currency";
  if (CURRENCY_PREFIXES.some((p) => lower.startsWith(p))) return "currency";
  if (DATE_SUFFIXES.some((s) => lower.endsWith(s))) return "date";
  if (ID_SUFFIXES.some((s) => lower.endsWith(s))) return "id";
  return "text";
};

// =============================================================================
// Cell renderers by format
// =============================================================================

const renderCurrency = (value: ValidAny): React.ReactNode => {
  if (value === null || value === undefined || value === "") return "—";
  const num = typeof value === "number" ? value : Number(value);
  return (
    <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
      {isNaN(num) ? String(value) : formatUSD(num)}
    </Typography>
  );
};

// renderDeduction renders an amount that the carrier convention treats as a
// deduction (adjustment_amount, denial_amount). Carrier PDFs frequently show
// magnitude only — "Less: Total Adjustments 433.92" — and the extractor
// captures the positive number, leaving the cash-impact sign encoded in the
// label. We force-display as non-positive here so reviewers see the actual
// impact: requested + signed_deduction = reimbursed. A genuinely-negative
// extracted value (e.g. carrier wrote "−433.92") is preserved as-is.
const renderDeduction = (value: ValidAny): React.ReactNode => {
  if (value === null || value === undefined || value === "") return "—";
  let num = typeof value === "number" ? value : Number(value);
  if (isNaN(num)) {
    return <Typography variant="body2">{String(value)}</Typography>;
  }
  if (num > 0) num = -num;
  return (
    <Typography
      variant="body2"
      sx={{ fontVariantNumeric: "tabular-nums", color: num < 0 ? "warning.dark" : undefined }}
    >
      {formatUSD(num)}
    </Typography>
  );
};

const DEDUCTION_FIELD_NAMES = new Set<string>(["adjustment_amount", "denial_amount"]);

const renderDate = (value: ValidAny): React.ReactNode => {
  if (value === null || value === undefined || value === "") return "—";
  return <Typography variant="body2">{formatDate(String(value))}</Typography>;
};

const renderId = (value: ValidAny): React.ReactNode => {
  if (value === null || value === undefined || value === "") return "—";
  return (
    <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8125rem" }}>
      {String(value)}
    </Typography>
  );
};

const renderText = (value: ValidAny): React.ReactNode => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return <Typography variant="body2">{JSON.stringify(value)}</Typography>;
  return <Typography variant="body2">{String(value)}</Typography>;
};

const RENDERERS: Record<FieldFormat, (value: ValidAny) => React.ReactNode> = {
  currency: renderCurrency,
  date: renderDate,
  id: renderId,
  text: renderText,
};

// =============================================================================
// Per-schema curated default columns
// =============================================================================

/**
 * Some schemas — Reimbursement in particular — have enough critical fields
 * that the default view becomes column-heavy (amounts, dates, references all
 * doubling up). When a schema has an entry here, the default view shows ONLY
 * these fields. Toggling "Show all columns" reverts to the full critical set
 * plus populated non-critical fields, so reviewers can opt into the wider
 * surface when they need it.
 *
 * Schemas without an entry fall back to the default behavior (all critical
 * fields visible).
 */
const DEFAULT_VISIBLE_FIELDS: Partial<Record<TargetSchema, string[]>> = {
  [TargetSchema.REIMBURSEMENT]: [
    // Claimant cross-ref column is prepended separately below — these are the
    // schema fields shown alongside it. Order matches reading flow:
    // identification → amount story → when paid → who got paid.
    "requested_amount",
    "reimbursement_amount",
    "payment_date",
    "provider",
  ],
};

// =============================================================================
// Composite field suppression
// =============================================================================

/**
 * When a composite field is populated, suppress its constituent parts.
 * E.g. if `claimant_name` has data, hide `claimant_first_name` and `claimant_last_name`.
 */
const COMPOSITE_FIELD_RULES: { composite: string; parts: string[] }[] = [
  { composite: "claimant_name", parts: ["claimant_first_name", "claimant_last_name"] },
  {
    composite: "claimant_related_subscriber_name",
    parts: ["claimant_related_subscriber_first_name", "claimant_related_subscriber_last_name"],
  },
];

const getSuppressedFields = (fieldsWithData: Set<string>): Set<string> => {
  const suppressed = new Set<string>();
  for (const rule of COMPOSITE_FIELD_RULES) {
    if (fieldsWithData.has(rule.composite)) {
      rule.parts.forEach((part) => suppressed.add(part));
    }
  }
  return suppressed;
};

/**
 * Same composite-field suppression the column builder applies, but operating on
 * a flat record data map rather than a schema-filtered field scan. Exposed so
 * the expand panel can hide the same redundant fields (e.g. claimant_first_name
 * when claimant_name is present).
 */
export const getSuppressedFieldsForData = (data: Record<string, ValidAny>): Set<string> => {
  const fieldsWithData = new Set<string>();
  for (const [key, value] of Object.entries(data)) {
    if (value !== null && value !== undefined && value !== "") fieldsWithData.add(key);
  }
  return getSuppressedFields(fieldsWithData);
};

// =============================================================================
// Cross-reference columns
// =============================================================================

/**
 * Defines a field from a related schema that should appear as a leading
 * column when viewing a given target schema.
 */
const CROSS_REFERENCE_COLUMNS: Partial<
  Record<TargetSchema, { field: string; fromSchema: TargetSchema; label: string }>
> = {
  [TargetSchema.REIMBURSEMENT]: {
    field: "claimant_name",
    fromSchema: TargetSchema.CLAIMANT,
    label: "Claimant",
  },
  [TargetSchema.CLAIM_ITEM]: {
    field: "claimant_name",
    fromSchema: TargetSchema.CLAIMANT,
    label: "Claimant",
  },
};

// =============================================================================
// Column width heuristics
// =============================================================================

/**
 * CSS grid width for a column based on its format.
 *
 * Numeric / date / id columns get fixed widths; free-form text flexes via
 * minmax() so it has a usable floor before the table starts horizontally
 * scrolling. The CompactTable wrapper uses overflow-x:auto, so once a row's
 * total width exceeds the container the table scrolls instead of compressing
 * cells into illegibility.
 */
const widthFor = (name: string, format: FieldFormat, isFirst: boolean): string => {
  if (format === "currency") return "120px";
  if (format === "date") return "120px";
  if (format === "id") return "150px";
  // Name-like fields get more room at the front of the table
  if (isFirst || name.includes("name")) return "minmax(180px, 1.5fr)";
  return "minmax(140px, 1fr)";
};

const alignFor = (format: FieldFormat): "left" | "right" => (format === "currency" ? "right" : "left");

// =============================================================================
// Dynamic column builder
// =============================================================================

/**
 * Build `CompactTableColumn<ExtractedRecord>[]` from extracted record data for a given schema.
 *
 * Default behavior: ONLY show the schema's critical fields (Required +
 * Recommended), regardless of whether the document populates them. Non-critical
 * populated fields are hidden — the grid stays focused on the load-bearing
 * columns and stays visually stable across documents.
 *
 * Pass `showAllColumns: true` to additionally surface populated non-critical
 * fields in source-document order, after the critical block. Reviewers
 * opt in via the "Show all columns" toggle in the review tab toolbar.
 */
export const buildDynamicColumns = (
  records: ExtractedRecord[],
  schema: TargetSchema,
  options: { showAllColumns?: boolean } = {}
): CompactTableColumn<ExtractedRecord>[] => {
  const { showAllColumns = false } = options;
  if (!records || records.length === 0) return [];

  const fieldOrder: string[] = [];
  const fieldHasData = new Set<string>();
  const fieldsSeen = new Set<string>();

  for (const record of records) {
    if (!record.extracted_fields) continue;
    for (const field of record.extracted_fields) {
      if (field.target_schema !== schema) continue;
      const name = field.target_field;
      if (!fieldsSeen.has(name)) {
        fieldsSeen.add(name);
        fieldOrder.push(name);
      }
      if (
        !fieldHasData.has(name) &&
        field.extracted_value !== null &&
        field.extracted_value !== undefined &&
        field.extracted_value !== ""
      ) {
        fieldHasData.add(name);
      }
    }
  }

  const suppressed = getSuppressedFields(fieldHasData);

  const criticalEntries = CRITICAL_FIELDS[schema] ?? [];
  const criticalityRank = (() => {
    const rankMap = new Map<string, number>();
    for (const entry of criticalEntries) {
      rankMap.set(entry.field, entry.severity === "required" ? 0 : 1);
    }
    return (field: string): number => rankMap.get(field) ?? 2;
  })();

  // Always-show columns for ALL critical fields (Required + Recommended)
  // regardless of whether this document populates them. Stable visual pattern
  // across documents so reviewers learn the layout; empty cells render as
  // em-dashes via the standard renderers.
  //
  // Composite alternatives (claimant_name / claimant_first_name+last):
  //   - if any composite member is populated, show ALL populated members
  //   - if none are populated, show the primary as a placeholder
  // Required slots come first within the always-show block, then recommended,
  // each in CRITICAL_FIELDS entry order.
  //
  // Schemas listed in DEFAULT_VISIBLE_FIELDS short-circuit this default and
  // render the curated list instead — used by Reimbursement to keep the
  // default view to a small, scannable column set. Toggling "Show all
  // columns" reverts to the full critical block + populated non-critical
  // fields, the same behavior other schemas always get.
  const curatedDefaults = DEFAULT_VISIBLE_FIELDS[schema];
  const alwaysShowFields: string[] = [];
  const alwaysShowSet = new Set<string>();

  if (curatedDefaults && !showAllColumns) {
    for (const f of curatedDefaults) {
      if (suppressed.has(f)) continue;
      alwaysShowFields.push(f);
      alwaysShowSet.add(f);
    }
  } else {
    for (const primary of criticalEntries) {
      if (primary.compositeAlternativeFor) continue;
      if (primary.severity !== "required" && primary.severity !== "recommended") continue;
      const composites = criticalEntries.filter((e) => e.compositeAlternativeFor === primary.field);
      const allMembers = [primary, ...composites];
      const populatedMembers = allMembers.filter((m) => fieldHasData.has(m.field));
      const fieldsToShow = populatedMembers.length > 0 ? populatedMembers.map((m) => m.field) : [primary.field];
      for (const f of fieldsToShow) {
        if (suppressed.has(f)) continue;
        alwaysShowFields.push(f);
        alwaysShowSet.add(f);
      }
    }
    // Sort the always-show set by criticality so Required precedes Recommended.
    alwaysShowFields.sort((a, b) => criticalityRank(a) - criticalityRank(b));
  }

  // Non-critical populated fields, only when the reviewer toggled "Show all
  // columns". Source-document first-appearance order preserved.
  const populatedRest = showAllColumns
    ? fieldOrder.filter((name) => fieldHasData.has(name) && !suppressed.has(name) && !alwaysShowSet.has(name))
    : [];

  const allFields = [...alwaysShowFields, ...populatedRest];

  const columns: CompactTableColumn<ExtractedRecord>[] = allFields.map((name, idx) => {
    const format = detectFieldFormat(name);
    // Adjustment / denial amounts are deductions by domain convention even
    // when the carrier PDF shows magnitude only ("Less: Total Adjustments").
    // Route them through renderDeduction so the displayed sign matches cash
    // impact regardless of which convention the source used. AB#4455.
    const renderer = format === "currency" && DEDUCTION_FIELD_NAMES.has(name) ? renderDeduction : RENDERERS[format];
    return {
      key: name,
      label: formatToTitleCase(name),
      width: widthFor(name, format, idx === 0),
      align: alignFor(format),
      render: (record: ExtractedRecord) => renderer(getRecordData(record)[name]),
    };
  });

  // Prepend cross-reference column if configured and data exists
  const crossRef = CROSS_REFERENCE_COLUMNS[schema];
  if (crossRef) {
    const hasData = records.some((r) =>
      r.extracted_fields?.some(
        (f) => f.target_schema === crossRef.fromSchema && f.target_field === crossRef.field && f.extracted_value
      )
    );
    if (hasData) {
      columns.unshift({
        key: `__xref_${crossRef.field}`,
        label: crossRef.label,
        width: "minmax(180px, 1.5fr)",
        render: (record: ExtractedRecord) => renderText(getRecordData(record)[crossRef.field]),
      });
    }
  }

  return columns;
};

/**
 * Helper to build claimant lookup from extracted records.
 * Maps claimant record ID → display name for cross-referencing.
 *
 * Still used by the issues panel for record labeling.
 */
export const buildClaimantLookup = (claimants: ExtractedRecord[]): Record<string, string> => {
  const lookup: Record<string, string> = {};
  claimants.forEach((claimant) => {
    const data = getRecordData(claimant);
    const id = data.id as string | undefined;
    const name =
      [data.claimant_first_name, data.claimant_last_name].filter(Boolean).join(" ") ||
      (data.claimant_name as string) ||
      "";
    if (id && name) {
      lookup[id] = name;
    }
  });
  return lookup;
};

/**
 * The column that identifies a claimant on the redesigned claimant grid
 * (#7435 AC1). Templates map either `claimant_name` or the composite pair
 * `claimant_first_name` + `claimant_last_name` (criticalFields.ts tells
 * reviewers to do exactly that). With the pair, buildDynamicColumns emits two
 * columns and no `claimant_name`, so a positional pick showed only the first
 * name and no sub-line (!4924 review). This returns a single column keyed
 * `claimant_name` either way, so RecordReviewGrid's ClaimantNameCell wrapping
 * applies to both shapes.
 */
export const selectClaimantNameColumn = (
  columns: CompactTableColumn<ExtractedRecord>[]
): CompactTableColumn<ExtractedRecord> | undefined => {
  const composite = columns.find((c) => c.key === "claimant_name");
  if (composite) return composite;
  const first = columns.find((c) => c.key === "claimant_first_name");
  const last = columns.find((c) => c.key === "claimant_last_name");
  if (first || last) {
    return {
      key: "claimant_name",
      label: "Claimant",
      width: (first ?? last)!.width,
      align: "left",
      render: (record: ExtractedRecord) => {
        const data = getRecordData(record);
        const full = [data.claimant_first_name, data.claimant_last_name].filter(Boolean).join(" ");
        return full || "—";
      },
    };
  }
  return columns[0];
};
